import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import axiosInstance from "../utils/axiosInstance";
import { AppButton, PageShell, SectionCard } from "../components/ui";
import "./css/Admin.css";

const LOOKUP_MODES = [
  { id: "name", label: "By Name" },
  { id: "gameCommanderId", label: "By Game Commander ID" },
];

const Admin = () => {
  const { user, isAuthInitializing } = useContext(AuthContext);
  const navigate = useNavigate();
  const isSuperUser = String(user?.role || "") === "superUser";

  const [lookupMode, setLookupMode] = useState("name");
  const [gameCommanderId, setGameCommanderId] = useState("");
  const [name, setName] = useState("");
  const [nationNumber, setNationNumber] = useState("");
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupCandidates, setLookupCandidates] = useState([]);

  useEffect(() => {
    if (isAuthInitializing) return;
    if (!isSuperUser) {
      navigate("/home");
    }
  }, [isAuthInitializing, isSuperUser, navigate]);

  const lookupHint = useMemo(() => {
    if (lookupMode === "gameCommanderId") return "Enter numeric game commander ID";
    return "Enter exact commander name. Nation number is recommended when names are duplicated.";
  }, [lookupMode]);

  const handleLookup = async () => {
    setLookupError("");
    setLookupResult(null);
    setLookupCandidates([]);

    const params = {};
    if (nationNumber.trim()) params.nationNumber = nationNumber.trim();

    if (lookupMode === "gameCommanderId") {
      if (!gameCommanderId.trim()) {
        setLookupError("gameCommanderId is required for this lookup mode.");
        return;
      }
      params.gameCommanderId = gameCommanderId.trim();
    } else {
      if (!name.trim()) {
        setLookupError("name is required for this lookup mode.");
        return;
      }
      params.name = name.trim();
    }

    try {
      setIsLookingUp(true);
      const response = await axiosInstance.get("/commanders/admin/game-id-lookup", { params });
      setLookupResult(response?.data?.commander || null);
    } catch (error) {
      const status = Number(error?.response?.status || 0);
      if (status === 409) {
        setLookupError(error?.response?.data?.message || "Multiple matches found.");
        setLookupCandidates(Array.isArray(error?.response?.data?.matches) ? error.response.data.matches : []);
      } else {
        setLookupError(error?.response?.data?.message || "Lookup failed.");
      }
    } finally {
      setIsLookingUp(false);
    }
  };

  if (!isSuperUser) return null;

  return (
    <PageShell maxWidth="xl">
      <div className="admin-dashboard-page">
        <h1>Admin</h1>
        <p>SuperUser-only toolbox for operational and diagnostic utilities.</p>

        <div className="admin-cards-grid">
          <SectionCard className="admin-card">
            <h2>Commander Game ID Lookup</h2>
            <p>Retrieve `gameCommanderId` securely for targeted investigations.</p>

            <div className="admin-form-grid">
              <label>
                Lookup Mode
                <select
                  value={lookupMode}
                  onChange={(e) => setLookupMode(e.target.value)}
                >
                  {LOOKUP_MODES.map((mode) => (
                    <option key={mode.id} value={mode.id}>
                      {mode.label}
                    </option>
                  ))}
                </select>
              </label>

              {lookupMode === "gameCommanderId" && (
                <label>
                  Game Commander ID
                  <input
                    value={gameCommanderId}
                    onChange={(e) => setGameCommanderId(e.target.value)}
                    placeholder="123456789"
                  />
                </label>
              )}

              {lookupMode === "name" && (
                <label>
                  Commander Name
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Exact name"
                  />
                </label>
              )}

              <label>
                Nation Number (optional)
                <input
                  value={nationNumber}
                  onChange={(e) => setNationNumber(e.target.value)}
                  placeholder="1115"
                  inputMode="numeric"
                />
              </label>
            </div>

            <p className="admin-hint">{lookupHint}</p>

            <div className="admin-actions-row">
              <AppButton
                className="admin-primary-btn"
                onClick={handleLookup}
                disabled={isLookingUp}
              >
                {isLookingUp ? "Looking up..." : "Lookup"}
              </AppButton>
            </div>

            {lookupError ? <p className="admin-error">{lookupError}</p> : null}

            {lookupResult && (
              <div className="admin-result-block">
                <p><strong>Name:</strong> {lookupResult?.name || "Unknown"}</p>
                <p><strong>Game Commander ID:</strong> {lookupResult?.gameCommanderId ?? "Not set"}</p>
                <p><strong>Nation:</strong> {lookupResult?.nation?.number != null ? lookupResult.nation.number : (lookupResult?.nation?.name || "Unknown")}</p>
                <p><strong>Alliance:</strong> {lookupResult?.alliance?.acronym || lookupResult?.alliance?.name || "Unknown"}</p>
                <p><strong>Status:</strong> {String(lookupResult?.status || "active")}</p>
              </div>
            )}

            {lookupCandidates.length > 0 && (
              <div className="admin-result-block">
                <p><strong>Candidate Matches:</strong> {lookupCandidates.length}</p>
                <div className="admin-candidate-list">
                  {lookupCandidates.map((candidate) => (
                    <div key={String(candidate?._id || "")} className="admin-candidate-row">
                      <span>{candidate?.name || "Unknown"} ({candidate?.nation?.number != null ? candidate.nation.number : "Unknown Nation"})</span>
                      <span>gameCommanderId: {candidate?.gameCommanderId ?? "Not set"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </SectionCard>

          <SectionCard className="admin-card">
            <h2>More Admin Tools</h2>
            <p>This dashboard is designed for small operational tools.</p>
            <div className="admin-tool-links">
              <Link to="/admin/market">Market Orders</Link>
            </div>
          </SectionCard>
        </div>
      </div>
    </PageShell>
  );
};

export default Admin;
